import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

const statusColor = (s) => {
  if (s === 'pending') return { bg: '#6366f120', fg: '#6366f1' };
  if (s === 'active') return { bg: '#10b98120', fg: '#10b981' };
  return { bg: '#64748b20', fg: '#64748b' };
};

export default function MyTeamReportees() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/employees/my-reportees');
        if (!cancelled) setRows(Array.isArray(r.data?.data) ? r.data.data : []);
      } catch (e) {
        if (!cancelled) {
          toast.error(e.response?.data?.message || 'Failed to load reportees');
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        My reportees (HR)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Open an HR profile to edit pending hires. Approve when ready — only active employees appear in organization
        pickers. User accounts are created separately by an administrator if needed.
      </Typography>

      {!rows.length ? (
        <Alert severity="info">You have no direct reports with HR profiles, or your user is not linked to an employee record.</Alert>
      ) : (
        <Card variant="outlined">
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((emp) => {
                  const c = emp.Contact;
                  const name = c ? `${c.first_name || ''} ${c.last_name || ''}`.trim() : '—';
                  const st = emp.status || '—';
                  const colors = statusColor(st);
                  return (
                    <TableRow key={emp.id} hover>
                      <TableCell>{name}</TableCell>
                      <TableCell>{c?.email || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={String(st).toUpperCase()}
                          size="small"
                          sx={{ bgcolor: colors.bg, color: colors.fg, fontWeight: 600, fontSize: '0.65rem' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => navigate(`/my-team/employees/${emp.id}`)}>
                          Open
                        </Button>
                        {st === 'pending' && (
                          <Button size="small" onClick={() => navigate(`/my-team/employees/${emp.id}/edit`)}>
                            Edit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
}
